import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FaMapMarkedAlt, 
  FaUserFriends, 
  FaHeart, 
  FaMobileAlt,
  FaArrowLeft
} from 'react-icons/fa';
import UnifiedStarBackground from './UnifiedStarBackground';
import '../styles/FeatureDetails.css';

interface FeatureContent {
  title: string;
  icon: JSX.Element;
  description: string;
  benefits: string[];
  details: string[];
  image: string;
}

const featureContents: { [key: string]: FeatureContent } = {
  'city-spots': {
    title: 'City-wise Tourist Spots',
    icon: <FaMapMarkedAlt className="feature-detail-icon" />,
    description: 'Discover handpicked destinations across India with comprehensive guides and stunning visuals.',
    benefits: [
      'High-quality images of every destination',
      'Detailed information about attractions',
      'Local tips and recommendations',
      'Seasonal visit guides',
      'Popular local experiences'
    ],
    details: [
      'Explore over 50+ cities across India',
      'Virtual tours of popular attractions',
      'Updated information about timings and entry fees',
      'Best time to visit recommendations',
      'Local transportation guides'
    ],
    image: 'https://images.unsplash.com/photo-1524492412937-b28074a5d7da?w=1200'
  },
  'easy-navigation': {
    title: 'Easy Navigation & User-friendly Design',
    icon: <FaUserFriends className="feature-detail-icon" />,
    description: 'Experience seamless travel planning with our intuitive interface and smart navigation.',
    benefits: [
      'Intuitive user interface',
      'Smart search functionality',
      'Quick filters for destinations',
      'Easy booking process',
      'Interactive maps'
    ],
    details: [
      'Advanced search algorithms',
      'Customizable trip planners',
      'Real-time updates',
      'Mobile-responsive design',
      'Offline accessibility'
    ],
    image: 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=1200'
  },
  'personalized': {
    title: 'Personalized Experience',
    icon: <FaHeart className="feature-detail-icon" />,
    description: 'Create your perfect travel experience with personalized recommendations and custom features.',
    benefits: [
      'Personalized travel recommendations',
      'Custom itinerary planning',
      'Favorite destination lists',
      'Travel preference settings',
      'Personal travel diary'
    ],
    details: [
      'AI-powered recommendations',
      'Custom travel plans',
      'Personal travel history',
      'Preference-based suggestions',
      'Travel milestone tracking'
    ],
    image: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1200'
  },
  'mobile-friendly': {
    title: 'Mobile Friendly Experience',
    icon: <FaMobileAlt className="feature-detail-icon" />,
    description: 'Access TripGo!! anywhere, anytime with our fully responsive mobile platform.',
    benefits: [
      'Responsive design for all devices',
      'Fast loading pages',
      'Offline content access',
      'Easy sharing features',
      'Mobile notifications'
    ],
    details: [
      'Cross-platform compatibility',
      'Progressive Web App features',
      'Location-based services',
      'Mobile-optimized images',
      'Touch-friendly interface'
    ],
    image: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=1200'
  }
};

const FeatureDetails: React.FC = () => {
  const { featureId } = useParams<{ featureId: string }>();
  const navigate = useNavigate();
  const feature = featureId ? featureContents[featureId] : null;

  if (!feature) {
    return <div>Feature not found</div>;
  }

  return (
    <div className="feature-details">
      <UnifiedStarBackground variant="primary" />
      <div className="feature-details-container">
        <button className="back-button" onClick={() => navigate('/about')}>
          <FaArrowLeft /> Back to About
        </button>
        
        <div className="feature-header">
          {feature.icon}
          <h1>{feature.title}</h1>
        </div>

        <div className="feature-hero">
          <img src={feature.image} alt={feature.title} className="feature-image" />
          <p className="feature-description">{feature.description}</p>
        </div>

        <div className="feature-content">
          <div className="benefits-section">
            <h2>Key Benefits</h2>
            <ul className="benefits-list">
              {feature.benefits.map((benefit, index) => (
                <li key={index}>{benefit}</li>
              ))}
            </ul>
          </div>

          <div className="details-section">
            <h2>Detailed Features</h2>
            <ul className="details-list">
              {feature.details.map((detail, index) => (
                <li key={index}>{detail}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureDetails;